import { useEffect, useState } from "react";
import UserData from "../Controls/UserData";
import { Form, FormDate, FormActions, FormInput, FormTextArea } from "../Controls/Form";
import CustomButton from "../Controls/CustomButton";
import Help from "../Controls/Help";
import CmModal from "../Controls/CmModal";
import ConfirmDlg from "../Controls/confirm";
import EmployerAdminListItem from "../ListItems/EmployerAdminListItem";
import JobPostingListItem from "../ListItems/JobPostingListItem";
import JobPostingEditModal from "../Modals/JobPostingEditModal";
import API from "@src/API.js";
import { toast } from "react-toastify";

function emptyEmployer(id = 0) {
  return {
    ID: id,
    Name: "",
    Url: "",
    ShortName: "",
    Description: "",
    OpenDate: null,
    CloseDate: null,
  };
}

/**
 * Employer form body. Prefer EmployerEditModal for UI entry points.
 * @param {number} employerId - 0 for new, otherwise existing Employer.ID
 * @param {boolean} promptForAdmin - after create, open Add administrator (with Skip)
 * @param {() => void} onClose - called after save/cancel
 */
export default function EmployerEdit({ employerId = 0, promptForAdmin = false, onClose }) {
  const id = Number.parseInt(employerId, 10) || 0;
  const [userData] = useState(UserData.UserData() || {});
  const [lookups] = useState(() => UserData.Lookups() || {});
  const [employer, updateEmployer] = useState(() => emptyEmployer(id));
  const [admins, setAdmins] = useState([]);
  const [postings, setPostings] = useState([]);
  const [roles, setRoles] = useState([]);
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("info");
  const [jobPostingModalId, setJobPostingModalId] = useState(null);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [assigningAdminAfterCreate, setAssigningAdminAfterCreate] = useState(false);
  const [representative, updateRepresentative] = useState({});
  const [resolvedId, setResolvedId] = useState(id);
  const [removeTarget, setRemoveTarget] = useState(null); // { personId, name }
  const [removeBusy, setRemoveBusy] = useState(false);

  async function loadAdminData(employerIdToLoad) {
    if (!employerIdToLoad) {
      setAdmins([]);
      setPostings([]);
      setRoles([]);
      setPackages([]);
      return;
    }
    const adminResp = await API.getAsync("/Employer/" + employerIdToLoad + "/Admin");
    if (adminResp?.ok && adminResp.jsonData) {
      const data = adminResp.jsonData;
      setAdmins(data.Admins || data.admins || []);
      setPostings(data.Postings || data.postings || []);
      setRoles(data.Roles || data.roles || []);
      setPackages(data.Packages || data.packages || []);
    }
  }

  useEffect(() => {
    if (!id) {
      setAdmins([]);
      setPostings([]);
      setRoles([]);
      setPackages([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const fromCache = (userData.employers || []).find((e) => Number(e.ID || e.id) === id);
      if (fromCache && !cancelled) {
        updateEmployer({ ...emptyEmployer(id), ...fromCache, ID: id });
      }
      const [employerResp, adminResp] = await Promise.all([
        API.getAsync("/Employer/" + id),
        API.getAsync("/Employer/" + id + "/Admin"),
      ]);
      if (cancelled) return;
      if (employerResp?.ok && employerResp.jsonData) {
        updateEmployer({ ...emptyEmployer(id), ...employerResp.jsonData, ID: id });
      }
      let loadedAdmins = [];
      if (adminResp?.ok && adminResp.jsonData) {
        const data = adminResp.jsonData;
        loadedAdmins = data.Admins || data.admins || [];
        setAdmins(loadedAdmins);
        setPostings(data.Postings || data.postings || []);
        setRoles(data.Roles || data.roles || []);
        setPackages(data.Packages || data.packages || []);
      }
      // After creating an employer, prompt for an admin — do not assume the current user.
      if (promptForAdmin && loadedAdmins.length === 0) {
        setTab("admins");
        setAssigningAdminAfterCreate(true);
        updateRepresentative({});
        setShowAddAdmin(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, userData.employers, promptForAdmin]);

  function close() {
    onClose?.();
  }

  function closeAddAdmin(skipped = false) {
    setShowAddAdmin(false);
    updateRepresentative({});
    if (assigningAdminAfterCreate) {
      setAssigningAdminAfterCreate(false);
      if (skipped) close();
    }
  }

  async function onDoneAsync() {
    if (busy) return;
    setBusy(true);
    const wasNew = !(Number(employer.ID) > 0);
    try {
      const payload = { ...employer, ID: employer.ID || 0 };
      const resp = await API.postAsync("/Employer", payload);
      if (resp?.ok) {
        const savedId =
          Number(resp.jsonData?.id ?? resp.jsonData?.ID ?? payload.ID) || 0;
        const saved = { ...payload, ...(resp.jsonData || {}), ID: savedId };
        toast.success("Employer saved");
        const list = userData.employers || [];
        const idx = list.findIndex((e) => Number(e.ID || e.id) === savedId);
        if (idx >= 0) list[idx] = saved;
        else list.push(saved);
        userData.employers = list;
        window.sessionStorage.setItem("UserData", JSON.stringify(userData));
        updateEmployer(saved);
        setResolvedId(savedId);
        if (wasNew && savedId > 0) {
          setTab("admins");
          setAssigningAdminAfterCreate(true);
          updateRepresentative({});
          setShowAddAdmin(true);
          await loadAdminData(savedId);
        } else {
          close();
        }
      } else {
        toast.error(resp?.jsonData?.message || "Could not save employer");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onAddAdminAsync() {
    const employerIdForAdmin = resolvedId || id;
    const email = (representative.email || representative.Email || "").trim();
    if (!email) {
      toast.error("Email is required");
      return;
    }

    const payload = {
      ...representative,
      employerId: employerIdForAdmin,
      firstName: representative.firstName ?? representative.FirstName,
      lastName: representative.lastName ?? representative.LastName,
      email,
    };

    const resp = await API.postAsync("/Person", payload);
    if (!resp || !resp.ok) {
      const msg =
        resp?.jsonData?.error ||
        resp?.jsonData?.message ||
        "There was an error adding this administrator. Please try again later.";
      toast.error(msg);
      return;
    }

    const action = (resp.jsonData?.action || resp.jsonData?.Action || "").toLowerCase();
    if (action === "invited") {
      toast.success(
        "Invite queued. We'll email them to create an account and help manage this company."
      );
    } else if (action === "associated") {
      toast.success(
        resp.jsonData?.alreadyAssociated || resp.jsonData?.AlreadyAssociated
          ? "That person is already associated with this company."
          : "That person is now an administrator for this company."
      );
    } else {
      toast.success("Administrator added.");
    }
    const afterCreate = assigningAdminAfterCreate;
    closeAddAdmin(false);
    await loadAdminData(employerIdForAdmin);
    if (afterCreate) close();
  }

  async function onConfirmRemoveAdmin(response) {
    if (response !== "Delete") {
      setRemoveTarget(null);
      return;
    }
    const personId = Number(removeTarget?.personId) || 0;
    const employerIdForAdmin = resolvedId || id;
    setRemoveTarget(null);
    if (!personId || !employerIdForAdmin || removeBusy) return;
    setRemoveBusy(true);
    try {
      const resp = await API.deleteAsync(`/Employer/${employerIdForAdmin}/Admin/${personId}`);
      if (!resp?.ok) {
        toast.error(resp?.jsonData?.message || "Could not remove administrator");
        return;
      }
      toast.success("Administrator rights removed.");
      await loadAdminData(employerIdForAdmin);
    } finally {
      setRemoveBusy(false);
    }
  }

  const adminCount = admins.length;
  const postingCount = postings.length;
  const employmentTypes = lookups.employmentType || lookups.EmploymentType || [];
  const employerName = employer.Name || employer.name || "";
  const effectiveId = resolvedId || id;

  return (
    <div className="cm-modal-embedded">
      <Form className="cm-modal-form" object={employer} onUpdate={updateEmployer}>
        <div className="cm-modal-tabs" role="tablist" aria-label="Employer sections">
          <button
            type="button"
            role="tab"
            id="employer-tab-info"
            aria-selected={tab === "info"}
            aria-controls="employer-panel-info"
            className={tab === "info" ? "is-active" : undefined}
            data-testid="employer-tab-info"
            onClick={() => setTab("info")}
          >
            Info
          </button>
          <button
            type="button"
            role="tab"
            id="employer-tab-admins"
            aria-selected={tab === "admins"}
            aria-controls="employer-panel-admins"
            className={tab === "admins" ? "is-active" : undefined}
            data-testid="employer-tab-admins"
            onClick={() => setTab("admins")}
            disabled={!effectiveId}
          >
            Administrators{adminCount > 0 ? ` (${adminCount})` : ""}
          </button>
          <button
            type="button"
            role="tab"
            id="employer-tab-postings"
            aria-selected={tab === "postings"}
            aria-controls="employer-panel-postings"
            className={tab === "postings" ? "is-active" : undefined}
            data-testid="employer-tab-postings"
            onClick={() => setTab("postings")}
            disabled={!effectiveId}
          >
            Job Postings{postingCount > 0 ? ` (${postingCount})` : ""}
          </button>
        </div>

        <div className="cm-modal-tab-panels">
          <div
            id="employer-panel-info"
            role="tabpanel"
            aria-labelledby="employer-tab-info"
            aria-hidden={tab !== "info"}
            className={`cm-modal-tab-panel${tab === "info" ? " is-active" : ""}`}
            {...(tab !== "info" ? { inert: "" } : {})}
          >
            <Help id="2143" />
            <FormInput label="Employer Name" field="Name" />
            <FormInput label="Url" field="Url" />
            <FormInput label="Short Name" field="ShortName" />
            <FormTextArea label="Description" field="Description" />
            <FormDate label="Date Opened" field="OpenDate" />
            <FormDate label="Date Closed" field="CloseDate" />
          </div>

          <div
            id="employer-panel-admins"
            role="tabpanel"
            aria-labelledby="employer-tab-admins"
            aria-hidden={tab !== "admins"}
            className={`cm-modal-tab-panel${tab === "admins" ? " is-active" : ""}`}
            data-testid="employer-admins"
            {...(tab !== "admins" ? { inert: "" } : {})}
          >
            {effectiveId > 0 ? (
              adminCount > 0 ? (
                <ul className="employer-modal-list">
                  {admins.map((person) => (
                    <EmployerAdminListItem
                      key={person.PersonID ?? person.personID ?? person.personId}
                      admin={person}
                      removeBusy={removeBusy}
                      onRemove={(personId, name) => setRemoveTarget({ personId, name })}
                    />
                  ))}
                </ul>
              ) : (
                <p className="byline">No administrators assigned yet.</p>
              )
            ) : (
              <p className="byline">Save the employer first to manage administrators.</p>
            )}
          </div>

          <div
            id="employer-panel-postings"
            role="tabpanel"
            aria-labelledby="employer-tab-postings"
            aria-hidden={tab !== "postings"}
            className={`cm-modal-tab-panel${tab === "postings" ? " is-active" : ""}`}
            data-testid="employer-postings"
            {...(tab !== "postings" ? { inert: "" } : {})}
          >
            {effectiveId > 0 ? (
              postingCount > 0 ? (
                <ul className="employer-modal-list">
                  {postings.map((jobPost) => (
                    <li key={jobPost.ID || jobPost.id}>
                      <JobPostingListItem
                        post={jobPost}
                        employmentTypes={employmentTypes}
                        onEdit={(postId) => setJobPostingModalId(postId)}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="byline">No job postings yet.</p>
              )
            ) : (
              <p className="byline">Save the employer first to manage job postings.</p>
            )}
          </div>
        </div>

        {tab === "info" ? (
          <FormActions
            doneLabel={busy ? "Saving…" : "Save"}
            onSaveAsync={async () => {
              await onDoneAsync();
            }}
            onCancelAsync={async () => {
              close();
            }}
          />
        ) : (
          <div className="form-actions">
            <CustomButton
              type="button"
              variant="contained"
              color="primary"
              data-testid={tab === "admins" ? "employer-add-admin" : "employer-add-job-posting"}
              onClick={() => {
                if (tab === "admins") {
                  updateRepresentative({});
                  setAssigningAdminAfterCreate(false);
                  setShowAddAdmin(true);
                } else {
                  setJobPostingModalId(0);
                }
              }}
            >
              Add
            </CustomButton>
          </div>
        )}
      </Form>

      {jobPostingModalId !== null ? (
        <JobPostingEditModal
          jobPostingId={jobPostingModalId}
          employerId={effectiveId}
          employerName={employerName}
          roles={roles}
          packages={packages}
          onClose={async () => {
            setJobPostingModalId(null);
            await loadAdminData(effectiveId);
          }}
        />
      ) : null}

      {showAddAdmin ? (
        <CmModal
          open
          bare
          onClose={() => closeAddAdmin(true)}
          title="Add Administrator"
          size="form"
          testId="employer-add-admin-modal"
        >
          <div className="cm-modal-embedded">
            <Form
              className="cm-modal-form"
              object={representative}
              onUpdate={updateRepresentative}
              onSubmitAsync={onAddAdminAsync}
              onCancelAsync={async () => {
                closeAddAdmin(true);
              }}
            >
              <p className="fieldHint">
                Optionally assign someone to manage this company. You can skip this and add an
                administrator later.
              </p>
              <FormInput type="text" field="firstName" placeholder="First Name" />
              <FormInput type="text" field="lastName" placeholder="Last Name" />
              <FormInput type="email" field="email" placeholder="Email" />
              <FormActions
                doneLabel="Add"
                cancelLabel="Skip"
                onCancelAsync={async () => {
                  closeAddAdmin(true);
                }}
              />
            </Form>
          </div>
        </CmModal>
      ) : null}

      {removeTarget ? (
        <ConfirmDlg
          title="Remove Administrator?"
          open
          delete
          cancel
          onResponse={onConfirmRemoveAdmin}
        >
          Remove administrator rights for {removeTarget.name}? This does not remove
          their employment or use of this company as an employer.
        </ConfirmDlg>
      ) : null}
    </div>
  );
}
